import {
  Cell,
  FieldValueGroupIds,
  FieldValueGroupReference,
  Form,
  FormField,
  GetAllFieldValuesDocument,
  GetCaseProfileGenRunsDocument,
  GetFieldHasFindingsDocument,
  GetFieldValuesDocument,
  Maybe,
  TagActionOrigin,
  TagView,
} from "generated/graphql";
import { contains, groupBy } from "helpers/coll";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectReviewConfigsByField } from "redux/slices/form.slice";
import { AppRoutes } from "AppRoutes";
import { arrayIndexHandle, StateHandle } from "StateHandle";
import update from "immutability-helper";
import { runGqlQuery } from "gqlClient";
import produce from "immer";
import {
  FormFieldValueGroupUI,
  FormFieldValuesUI,
  calculateDefaultRowViewState,
  UNGROUPED_CELL_ID,
  isNotUndefined,
} from "Backend";
import { useSnackbar } from "notistack";
import { CombinedError } from "urql";
import { SetHook, useSet } from "./useSet";
import { selectTags } from "redux/slices/clientLens";
import { selectIsSupport } from "redux/slices/session";
const MAX_VALUE_GROUPS_PER_FIELD = 1_000;

export interface ProfileGenRunInfo {
  runCount: number;
  lastRun?: {
    timestamp: string;
    pageSetCount: number;
  };
}
export type ProfileGenRunMap = { [fieldId: number]: ProfileGenRunInfo };

export type FieldUIState = {
  groupByCellId: number;
  groupExpanded?: boolean;
  expandedGroupNames: string[];
  filterByStarred: boolean;
  groupSorts: {
    [cellValue: string]: {
      cell: Cell;
      sortDir: "asc" | "desc";
    };
  };
};

interface FieldValueGroup {
  fieldId: number;
  valueGroupId?: number;
}

export interface UseCaseForm {
  autoScriptRunIdsSet: SetHook<string>;
  linkToReview: (fieldId: number) => string | undefined;
  stateHandle: { [x in number]: StateHandle<FormFieldValuesUI> };
  resetAllFieldsServer: () => Promise<FormFieldValuesUI[] | undefined>;
  resetFieldLocal: (fieldIndex: number) => void;
  allFieldReferences: FieldValueGroupReference[];
  fieldValuesCount: number;
  serverFieldValues: FormFieldValuesUI[];
  reloadServerValues: () => Promise<FormFieldValuesUI[]>;
  resetFieldsServer: (fieldIds: number[]) => Promise<FormFieldValuesUI[]>;
  loadFieldHasFindings: (fieldId: number) => Promise<void>;
  loadAllFieldsHaveFindings: () => Promise<void>;
  fieldsForValueGroups: (valueGroupSerialIds: number[]) => {
    valueGroups: FormFieldValueGroupUI[];
    formField: FormField;
  }[];
  fieldHasFindingsMap: { [fieldId: number]: boolean };
  fieldProfileGenRunsMap: ProfileGenRunMap;
  loadProfileGenRuns: () => Promise<void>;
  intakeViewerBlockIndexState: StateHandle<number | undefined | null>;
  focusFieldReferenceState: StateHandle<number | undefined>;
  focusFieldValueGroupState: StateHandle<FieldValueGroup | undefined>;
  outlineOpenState: StateHandle<boolean>;
  mutationInProgressHandle: StateHandle<boolean>;
  fieldUIStateMapHandle: StateHandle<{ [fieldId: number]: FieldUIState }>;
  caseId: number;
  serverValuesForField: (fieldId: number | undefined) => FormFieldValuesUI | undefined;
  updateValueGroups: (fieldId: number, update: (valueGroups: FormFieldValueGroupUI[]) => void) => void;
  updateValueGroup: (
    fieldId: number,
    yesNoValue: Maybe<boolean> | undefined,
    update: (valueGroups: FormFieldValueGroupUI[]) => void
  ) => void;
  setFieldYesNoToFalse: (fieldId: number, update: (field: FormFieldValuesUI) => void) => void;
  sortValueGroups: (
    fieldId: number,
    valueGroupUpdates: { groupId: number; rank: number }[],
    sort: (valueGroups: FormFieldValueGroupUI[], valueGroupUpdates: { groupId: number; rank: number }[]) => void
  ) => void;
  sortValueGroupsFromServerSort: (
    fieldId: number,
    fieldValueGroupIds: FieldValueGroupIds[],
    sort: (fieldValuesUI: FormFieldValuesUI, fieldValueGroupIds: FieldValueGroupIds[]) => void
  ) => void;
  fieldTagMapHandle: StateHandle<Map<number, TagView[]>>;
}

function useCaseForm({ lensId, caseId, form }: { lensId: number; caseId: number; form: Form }): UseCaseForm {
  const { enqueueSnackbar } = useSnackbar();
  const autoScriptRunIdsSet = useSet<string>([]);
  const formFieldsMap = new Map<number, FormField>(form.fields.map((ff) => [ff.id, ff]));
  const allFieldIds = Array.from(formFieldsMap.keys());
  const serverHandle = useState<FormFieldValuesUI[]>([]);
  const [serverValues, setServerValues] = serverHandle;
  const serverFieldValues = serverValues.map((sv) => sv.valueGroups).flat();
  const tags = useAppSelector(selectTags);
  const isSupport = useAppSelector(selectIsSupport);
  const valueGroupBySerialIdMap = new Map(serverFieldValues.map((sfv) => [sfv.serialId, sfv]));
  const [fieldHasFindingsMap, setFieldHasFindingsMap] = useState<{ [k: number]: boolean }>({});
  const reviewConfigs = useAppSelector(selectReviewConfigsByField);
  const fieldValuesState = useState<FormFieldValuesUI[]>([]);
  const [fieldValues, setFieldValues] = fieldValuesState;
  const intakeViewerBlockIndexState = useState<number | undefined | null>();
  const focusFieldReferenceState = useState<number | undefined>();
  const focusFieldValueGroupState = useState<FieldValueGroup | undefined>();
  const outlineOpenState = useState<boolean>(true);
  const profileGenRunsMapState = useState<ProfileGenRunMap>({});
  const mutationInProgressHandle = useState(false);
  const fieldTagMapHandle = useState(new Map());
  const fieldUIStateMapHandle = useState<{ [fieldId: number]: FieldUIState }>(
    allFieldIds.reduce((acc, fieldId) => {
      const formField = form.fields.find((f) => f.id === fieldId);
      const fieldUIState: FieldUIState = {
        groupByCellId:
          formField?.groupByCellId !== undefined && formField?.groupByCellId !== null
            ? formField.groupByCellId
            : UNGROUPED_CELL_ID,
        expandedGroupNames: [],
        filterByStarred: false,
        groupSorts: {},
      };
      acc[fieldId] = fieldUIState;
      return acc;
    }, {})
  );

  const fieldsForValueGroups = (
    valueGroupSerialIds: number[]
  ): {
    valueGroups: FormFieldValueGroupUI[];
    formField: FormField;
  }[] => {
    const valueGroups = valueGroupSerialIds
      .map((serialId) => valueGroupBySerialIdMap.get(serialId))
      .filter(isNotUndefined);
    return Object.values(
      valueGroups.reduce((prev, valueGroup) => {
        if (prev[valueGroup.fieldId]) {
          prev[valueGroup.fieldId].valueGroups.push(valueGroup);
        } else {
          const formField = formFieldsMap.get(valueGroup.fieldId);
          if (formField !== undefined) {
            prev[valueGroup.fieldId] = {
              valueGroups: [valueGroup],
              formField,
            };
          }
        }
        return prev;
      }, {})
    );
  };

  const loadAllFieldsHaveFindings = async () => {
    if (form !== undefined) {
      form.fields.map(async (field) => {
        try {
          const res = await runGqlQuery(GetFieldHasFindingsDocument, {
            caseId,
            formId: form.id,
            fieldId: field.id,
          });
          const hasFindings = res.case.form.field.hasFindings;
          setFieldHasFindingsMap((prev) =>
            produce(prev, (draft) => {
              draft[field.id] = hasFindings;
            })
          );
        } catch (e) {
          enqueueSnackbar("There was a problem loading the field findings. Please try again later.", {
            variant: "error",
          });
        }
      });
    }
  };

  const loadFieldHasFindings = async (fieldId: number) => {
    if (!form) return;
    runGqlQuery(GetFieldHasFindingsDocument, {
      caseId,
      formId: form.id,
      fieldId: fieldId,
    })
      .then((res) => {
        const hasFindings = res.case.form.field.hasFindings;
        setFieldHasFindingsMap((prev) =>
          produce(prev, (draft) => {
            draft[fieldId] = hasFindings;
          })
        );
      })
      .catch(() => {
        enqueueSnackbar("There was a problem loading the field findings. Please try again later.", {
          variant: "error",
        });
      });
  };

  const fetchFieldValues = async (fieldIds: number[]) => {
    if (form !== undefined) {
      const values: FormFieldValuesUI[] = await Promise.all(
        fieldIds.map((fieldId) =>
          runGqlQuery(GetFieldValuesDocument, {
            caseId,
            formId: form.id,
            fieldId: fieldId,
            valueGroupOffset: 0,
            valueGroupLimit: MAX_VALUE_GROUPS_PER_FIELD,
          })
            .then((res) => {
              const fieldValues = res.case.form.field.values;
              const valueGroups = fieldValues.valueGroups;

              return {
                ...fieldValues,
                valueGroups: valueGroups.map((v, index) => ({
                  ...v,
                  id: v.groupIndex,
                  viewState: calculateDefaultRowViewState(valueGroups.length),
                  responseNumber: index + 1,
                  availableTags: [],
                  tags: v.tags.map((tag) => ({
                    ...tag,
                    origin: TagActionOrigin.AppUser, // FIXME more correct to pass this from the backend.
                  })),
                })),
              };
            })
            .catch((error: CombinedError) => {
              if (!error.message.includes("case values")) {
                enqueueSnackbar("There was a problem loading the field values. Please try again later.", {
                  variant: "error",
                });
              }
              return {
                fieldId: fieldId,
                valueGroupOffset: 0,
                valueGroupTotal: 0,
                valueGroups: [],
                summaryData: {},
              };
            })
        )
      );
      const foundValues = values.filter((v) => v.fieldId !== -1);
      const foundFieldIds = foundValues.map((v) => v.fieldId);
      setServerValues((prev) => {
        const filtered = prev.filter((f) => !contains(foundFieldIds, f.fieldId));
        const updated = [...filtered, ...foundValues];
        return updated;
      });
      return foundValues;
    } else {
      return [];
    }
  };

  const fetchAllServerValues = async () => {
    if (form) {
      try {
        const response = await runGqlQuery(GetAllFieldValuesDocument, {
          caseId,
          formId: form.id,
          valueGroupOffset: 0,
          valueGroupLimit: MAX_VALUE_GROUPS_PER_FIELD,
        });
        const fieldTagMap = new Map();
        const values = response.case.form.allFieldValues.map((value) => {
          const fieldTags = tags.filter((t) => t.fields.includes(value.fieldId));
          const availableTags =
            fieldTags.filter((t) => isSupport || !t.supportOnlyFields.includes(value.fieldId)) || [];
          fieldTagMap.set(value.fieldId, availableTags);
          return {
            ...value,
            valueGroups: value.valueGroups.map((vg, index) => ({
              ...vg,
              id: vg.groupIndex,
              viewState: calculateDefaultRowViewState(value.valueGroups.length),
              responseNumber: index + 1,
              availableTags,
              tags: vg.tags.map((tag) => ({
                ...tag,
                origin: TagActionOrigin.AppUser, // FIXME more correct to pass this from the backend.
              })),
            })),
          };
        });
        setServerValues(values);
        fieldTagMapHandle[1](fieldTagMap);
        return values;
      } catch (e) {
        enqueueSnackbar("There was a problem loading the field values. Please try again later.", {
          variant: "error",
        });
        return [];
      }
    } else {
      return [];
    }
  };

  const fieldsStateHandles = useMemo(() => {
    if (!form) return {};
    return form.fields.reduce((handle, field) => {
      handle[field.id] = arrayIndexHandle(fieldValuesState, field.id);
      return handle;
    }, {});
  }, [fieldValues, form]);

  const serverValuesForField = useCallback(
    (fieldId: number | undefined) => serverValues.find((sv) => sv.fieldId === fieldId),
    [form, serverValues]
  );

  const allFieldReferences: FieldValueGroupReference[] = useMemo(
    () =>
      fieldValues
        .map((fv) => fv.valueGroups || [])
        .flat()
        .map((f) => f.references || [])
        .flat(),
    [fieldValues]
  );

  const linkToReview = (fieldId: number): string | undefined => {
    const field = form && form.fields.find((f) => f.id === fieldId);
    if (!field) return undefined;
    const reviewConfig = reviewConfigs[field.id];
    if (reviewConfig?.documentReview) {
      return AppRoutes.caseDocumentsBuilder({
        lensId,
        caseId,
        documentId: "select-document",
      });
    }

    if (fieldHasFindingsMap[field.id]) {
      const { familyOnly, hideNegated } = reviewConfig
        ? { familyOnly: reviewConfig.showFamily, hideNegated: reviewConfig.hideNegated }
        : { familyOnly: false, hideNegated: false };
      return AppRoutes.caseDetailReviewField(lensId, caseId, field.id, familyOnly, hideNegated);
    } else {
      return undefined;
    }
  };

  const resetFieldState = (values: FormFieldValuesUI[], fieldId: number) => {
    const backendValue = values.find((v) => v.fieldId === fieldId);
    if (backendValue) {
      return backendValue;
    } else {
      const empty: FormFieldValuesUI = {
        fieldId,
        yesNoValue: undefined,
        valueGroups: [],
        valueGroupOffset: 0,
        valueGroupTotal: 0,
        summaryData: {},
      };
      return empty;
    }
  };

  const resetFields = useCallback(
    (fieldIds: number[], values: FormFieldValuesUI[]) => {
      if (form) {
        const newFields = form.fields
          .filter((f) => contains(fieldIds, f.id))
          .map((field) => resetFieldState(values, field.id));
        setFieldValues((prev) =>
          produce(prev, (draft) => {
            newFields.forEach((updatedField) => {
              draft[updatedField.fieldId] = updatedField;
            });
          })
        );
      }
    },
    [form, serverValues]
  );

  const resetAllFields = useCallback(
    (values: FormFieldValuesUI[]) => resetFields(allFieldIds || [], values),
    [form, serverValues]
  );

  const resetAllFieldsServer: () => Promise<FormFieldValuesUI[]> = useCallback(async () => {
    if (form && serverValues) {
      const values = await fetchAllServerValues();
      resetAllFields(values);
      return values;
    } else {
      return Promise.resolve([]);
    }
  }, [form, serverValues, lensId, caseId]);

  /**
   * This should only be used when the form has changed
   */
  const resetFieldsServer: (fieldIds: number[]) => Promise<FormFieldValuesUI[]> = useCallback(
    async (fieldIds: number[]) => {
      if (form && serverValues) {
        const values = await fetchFieldValues(fieldIds);
        resetFields(fieldIds, values);
        return values;
      } else {
        return Promise.resolve([]);
      }
    },
    [form, serverValues, lensId, caseId]
  );

  const resetLocalFieldState = useCallback(
    async (fieldId: number) => {
      if (form) {
        const field = form.fields.find((f) => f.id === fieldId);
        if (field && serverValues) {
          const values = resetFieldState(serverValues, field.id);
          setFieldValues((prev) => update(prev, { [field.id]: { $set: values } }));
        }
      }
    },
    [form, serverValues]
  );

  const loadProfileGenRuns = async () => {
    await runGqlQuery(GetCaseProfileGenRunsDocument, { formId: form.id, caseId })
      .then((response) => {
        const rawRuns = response.case.form.profileGenRuns;
        const runPairs = [...groupBy(rawRuns, (run) => run.fieldId)].map(([fieldId, fieldRuns]) => {
          const lastRun = fieldRuns[fieldRuns.length - 1];
          const lastRunInfo = lastRun
            ? { timestamp: lastRun.createdAt, pageSetCount: lastRun.pageSetCount }
            : undefined;
          const runInfo: ProfileGenRunInfo = {
            runCount: fieldRuns.length,
            lastRun: lastRunInfo,
          };
          return [fieldId, runInfo] as const;
        });
        const runMap = runPairs.reduce<ProfileGenRunMap>((prev, [fieldId, info]) => ({ ...prev, [fieldId]: info }), {});
        profileGenRunsMapState[1](runMap);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    if (form) {
      void loadProfileGenRuns();
      resetAllFieldsServer().catch((error) => console.error("useCaseForm; failed to fetchAllServerVals", error));
    }
  }, [form?.id, caseId]);

  /** setServerValues Helpers */
  const updateValueGroups = (fieldId: number, update: (valueGroups: FormFieldValueGroupUI[]) => void) => {
    setServerValues((prev) =>
      produce(prev, (serverValues) => {
        const field = serverValues.find((fv) => fv.fieldId === fieldId);
        if (field !== undefined) {
          update(field.valueGroups);
        }
      })
    );
  };

  const updateValueGroup = (
    fieldId: number,
    yesNoValue: Maybe<boolean> | undefined,
    update: (valueGroups: FormFieldValueGroupUI[]) => void
  ) => {
    setServerValues((prev) =>
      produce(prev, (serverValues) => {
        const fieldValue = serverValues.find((fv) => fv.fieldId === fieldId);
        if (fieldValue !== undefined) {
          fieldValue.yesNoValue = yesNoValue;
          update(fieldValue.valueGroups);
        } else {
          const valueGroups = [];
          update(valueGroups);
          serverValues.push({
            fieldId,
            yesNoValue,
            valueGroupOffset: 0,
            valueGroups,
            valueGroupTotal: 0,
            summaryData: {},
          });
        }
      })
    );
  };

  const setFieldYesNoToFalse = (fieldId: number, update: (field: FormFieldValuesUI) => void) => {
    setServerValues((prev) =>
      produce(prev, (serverValues) => {
        const field = serverValues.find((fv) => fv.fieldId === fieldId);
        if (field !== undefined) {
          update(field);
        }
      })
    );
  };

  const sortValueGroups = (
    fieldId: number,
    valueGroupUpdates: { groupId: number; rank: number }[],
    sort: (valueGroups: FormFieldValueGroupUI[], valueGroupUpdates: { groupId: number; rank: number }[]) => void
  ) => {
    setServerValues((prev) =>
      produce(prev, (serverValues) => {
        const field = serverValues.find((fv) => fv.fieldId === fieldId);
        if (field !== undefined) {
          sort(field.valueGroups, valueGroupUpdates);
        }
      })
    );
  };

  const sortValueGroupsFromServerSort = (
    fieldId: number,
    fieldValueGroupIds: FieldValueGroupIds[],
    sort: (fieldValuesUI: FormFieldValuesUI, fieldValueGroupIds: FieldValueGroupIds[]) => void
  ) => {
    setServerValues((prev) =>
      produce(prev, (serverValues) => {
        const field = serverValues.find((fv) => fv.fieldId === fieldId);
        if (field !== undefined) {
          sort(field, fieldValueGroupIds);
        }
      })
    );
  };

  return {
    autoScriptRunIdsSet,
    linkToReview,
    fieldsForValueGroups,
    stateHandle: fieldsStateHandles,
    resetFieldLocal: resetLocalFieldState,
    mutationInProgressHandle,
    resetAllFieldsServer,
    allFieldReferences,
    serverFieldValues: serverHandle[0],
    reloadServerValues: fetchAllServerValues,
    resetFieldsServer,
    fieldValuesCount: fieldValues.length,
    loadAllFieldsHaveFindings,
    loadFieldHasFindings,
    fieldHasFindingsMap: fieldHasFindingsMap,
    fieldProfileGenRunsMap: profileGenRunsMapState[0],
    loadProfileGenRuns,
    intakeViewerBlockIndexState,
    focusFieldReferenceState,
    focusFieldValueGroupState,
    outlineOpenState,
    fieldUIStateMapHandle,
    caseId,
    serverValuesForField,
    updateValueGroups,
    updateValueGroup,
    setFieldYesNoToFalse,
    sortValueGroups,
    sortValueGroupsFromServerSort,
    fieldTagMapHandle,
  };
}

export default useCaseForm;
